import { FormEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  Integration,
  IntegrationTestResult,
  getGlobalIntegration,
  getStageIntegration,
  putGlobalIntegration,
  putStageIntegration,
  testIntegration,
} from "@/api/integrations";
import { Field, Input, Textarea } from "@/components/form";
import { ErrorBox, MutationError, RefreshFailed, Spinner, isLoadingOrPaused } from "@/components/Spinner";
import { formatDate } from "@/lib/dates";
import { Button, Notice, UnsavedBadge } from "@/components/ui";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

/**
 * Credential form for one third-party provider, in either scope.
 *
 * Entirely driven by the `fields` the backend describes, so a new provider
 * (or a new field on an existing one) needs no change here.
 *
 * Secrets are write-only: the form never receives one, shows whether one is
 * stored, and only sends the ones the operator actually typed. Leaving a
 * secret input blank therefore means "keep it", which is what an operator
 * fixing a neighbouring field expects.
 */
export function IntegrationForm({
  provider,
  scope,
}: {
  provider: string;
  scope?: { tenantId: string; stage: string };
}) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () =>
      scope
        ? ["integration", provider, scope.tenantId, scope.stage]
        : ["integration", provider, "global"],
    [provider, scope],
  );

  const query = useQuery<Integration>({
    queryKey,
    queryFn: () =>
      scope
        ? getStageIntegration(token, scope.tenantId, scope.stage, provider)
        : getGlobalIntegration(token, provider),
    enabled: Boolean(token),
  });
  const { data } = query;

  // Only what the operator edited is sent — an untouched field keeps its
  // stored value, so correcting one input can never clear another.
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<IntegrationTestResult | null>(null);

  // What is typed here and not stored: a field whose typed value is not the one on record (a secret
  // has none to show, so any text is a change; a public field is compared with its value). A field
  // typed into and put back is not a change — unlike ``dirty`` below, which only says that the
  // operator touched something and gates Save and Test as it always did. Several of these forms can
  // sit on one page (one card per provider, under a stage's own form): each reports for itself, and
  // the way out of the page is held while any does.
  const unsaved =
    data !== undefined &&
    Object.entries(edited).some(([name, value]) => {
      const field = data.fields.find((f) => f.name === name);
      return value !== (field?.secret ? "" : (field?.value ?? ""));
    });
  useUnsavedChanges(unsaved);

  useEffect(() => {
    setEdited({});
    setTestResult(null);
  }, [queryKey.join("/")]);

  const save = useMutation({
    mutationFn: () => {
      const fields = Object.entries(edited).map(([name, value]) => ({
        name,
        value,
      }));
      return scope
        ? putStageIntegration(token, scope.tenantId, scope.stage, provider, {
            fields,
          })
        : putGlobalIntegration(token, provider, { fields });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKey, updated);
      setEdited({});
      setTestResult(null);
    },
  });

  const probe = useMutation({
    mutationFn: () => testIntegration(token, provider, scope),
    onSuccess: setTestResult,
  });

  if (isLoadingOrPaused(query)) return <Spinner />;
  // Only an error with nothing to show replaces the form (see RefreshFailed):
  // a failed refetch must not hide the credentials the operator is typing.
  if (query.isLoadingError) return <ErrorBox error={query.error} />;
  if (!data) return null;

  const dirty = Object.keys(edited).length > 0;
  const inherited = Boolean(scope) && data.source !== "stage";

  // One failure on screen at a time: running the other action clears the last one's.
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    probe.reset();
    save.mutate();
  };

  // Un fournisseur SANS champ n'est pas un cas dégénéré : l'analyse des
  // paroles emprunte la clé de « Paroles et genres », et lui en redemander une
  // seconde ferait saisir deux fois le même secret sans garantir qu'ils
  // restent égaux. Il ne reste alors qu'un interrupteur — rendu par la carte,
  // pas par ce formulaire. Un bouton « Enregistrer » qui n'enverrait rien
  // serait pire qu'absent : il promettrait un geste.
  if (data.fields.length === 0) {
    return (
      <Notice tone="info">
        This service doesn't have its own credentials: it uses those of the
        provider it depends on. Only the switch above controls it.
      </Notice>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <RefreshFailed query={query} />
      {inherited && (
        <Notice tone="info">
          {data.source === "unset"
            ? "No account is configured: this environment won't have this service."
            : "This environment uses the shared account. Fill in the fields below to assign it a dedicated one."}
        </Notice>
      )}

      {data.fields.map((field) => {
        const value = edited[field.name];
        return (
          <Field
            key={field.name}
            label={field.label}
            help={
              field.secret
                ? field.configured
                  ? `Saved on ${formatDate(field.updatedAt, { zone: "local" })}. Leave blank to keep it.`
                  : "Never set."
                : undefined
            }
          >
            {field.multiline ? (
              <Textarea
                mono
                rows={6}
                value={value ?? ""}
                placeholder={field.configured ? "•••••••• already saved" : ""}
                onChange={(e) =>
                  setEdited((s) => ({ ...s, [field.name]: e.target.value }))
                }
              />
            ) : (
              <Input
                type={field.secret ? "password" : "text"}
                autoComplete="off"
                value={value ?? (field.secret ? "" : (field.value ?? ""))}
                placeholder={field.configured ? "•••••••• already saved" : ""}
                onChange={(e) =>
                  setEdited((s) => ({ ...s, [field.name]: e.target.value }))
                }
              />
            )}
          </Field>
        );
      })}

      <MutationError mutation={save} title="Could not save the credentials" />
      <MutationError mutation={probe} title="Could not test the connection" />
      {testResult && (
        // A result that appears after a click, announced without moving focus: a
        // success politely (`status`), a refusal or a doubt at once (`alert`).
        <Notice
          role={testResult.ok ? "status" : "alert"}
          tone={testResult.ok ? "success" : testResult.rejected ? "danger" : "warning"}
        >
          {testResult.message}
        </Notice>
      )}

      <div className="flex items-center justify-between gap-2 border-t border-line pt-3">
        <UnsavedBadge dirty={unsaved} />
        <div className="flex items-center gap-2">
          {data.testable && (
            <Button
              variant="secondary"
              loading={probe.isPending}
              // Probes what is STORED, so an unsaved edit would test the old
              // values and mislead.
              disabled={dirty || data.source === "unset"}
              onClick={() => {
                save.reset();
                probe.mutate();
              }}
              title={
                dirty
                  ? "Save first to test the stored values"
                  : undefined
              }
            >
              {probe.isPending ? "Testing…" : "Test"}
            </Button>
          )}
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <p className="text-xs text-ink-muted">
        Values are passed to the environment on its next deployment.
      </p>
    </form>
  );
}
